<script setup lang="ts">
import { computed, ref, watch } from 'vue';

import { SaleBadgeRound } from '@/shared/components/UI/badges';
import Image from '@/shared/components/UI/Image.vue';
import NumberSpinner from '@/shared/components/UI/NumberSpinner.vue';
import StockIndicator from '@/shared/components/UI/StockIndicator.vue';
import {
  PatternProduct,
  Variant
} from '@/shared/composables/patternProducts/patternProducts.types';
import { useTranslations } from '@/shared/composables/useTranslations';
// TODO: Enable productType
//import { productTypes } from '@/shared/constants/shopify.ts';
import { shopifyGidToId } from '@/shared/utils/shopify/id-formatter';
import {
  formatPriceStorefront,
  isCompareAtPriceHigher
} from '@/shared/utils/shopify/money-formatter';

import VariantSelector from './VariantSelector.vue';

const emit = defineEmits(['variant-change', 'quantity-change']);

const props = defineProps<{
  patternProduct: PatternProduct;
  index: number;
}>();

const { t } = useTranslations();

const currentQuantity = ref<number | undefined>(props.patternProduct.quantity);
const currentVariant = ref<Variant | null>(
  props.patternProduct.product?.variant || null
);

const productPageURL = computed(() => {
  const handle = props.patternProduct.product?.handle;
  if (!handle) return '';
  const query = variantURLParam.value;
  return query ? `/products/${handle}?${query}` : `/products/${handle}`;
});

const variantURLParam = computed(() => {
  return currentVariant.value
    ? `variant=${shopifyGidToId(currentVariant.value.id)}`
    : '';
});

const emitChange = (eventName: 'variant-change' | 'quantity-change') => {
  emit(eventName, {
    productId: props.patternProduct.product_id,
    variantId: currentVariant.value?.id,
    quantity: currentQuantity.value,
    index: props.index
  });
};

const handleVariantChange = (variant: Variant) => {
  currentVariant.value = variant;
  emitChange('variant-change');
};

const handleQuantityChange = (newQuantity: number) => {
  currentQuantity.value = newQuantity;
  emitChange('quantity-change');
};

const productInStock = computed(
  () => (currentVariant.value?.quantityAvailable || 0) > 0
);

const getTrackingItem = () => {
  if (!productInStock.value) return null;

  const rawPrice = parseFloat(currentVariant.value?.price?.amount ?? '');
  const price = Number.isFinite(rawPrice) ? rawPrice : 0;

  return {
    item_id: props.patternProduct.product_id,
    item_name: props.patternProduct.product?.title,
    item_category: props.patternProduct.product?.productType,
    item_variant: currentVariant.value?.title,
    price,
    quantity: currentQuantity.value
  };
};
const priceWithCurrencySymbol = computed(() => {
  const price = props.patternProduct?.product?.price;
  return price ? formatPriceStorefront(price) : '';
});

const compareAtPriceWithCurrencySymbol = computed(() => {
  const compareAtPrice = props.patternProduct?.product?.compareAtPrice;
  if (!parseFloat(compareAtPrice?.amount)) return '';

  return compareAtPrice ? formatPriceStorefront(compareAtPrice) : '';
});

const shouldShowCompareAtPrice = computed(() =>
  isCompareAtPriceHigher(
    props.patternProduct?.product?.price,
    props.patternProduct?.product?.compareAtPrice
  )
);

const imgSrc = computed(() => {
  const url =
    currentVariant.value?.image?.url ||
    props.patternProduct.product?.featuredImage?.url;
  if (url) {
    const imgSrc = new URL(url);
    imgSrc.searchParams.set('width', '200');
    imgSrc.searchParams.set('height', '200');
    imgSrc.searchParams.set('q', '80');
    return imgSrc.toString();
  }
  return '';
});

const getCartItem = () => {
  return currentVariant.value && currentQuantity.value && productInStock.value
    ? {
        id: shopifyGidToId(currentVariant.value.id || ''),
        quantity:
          currentVariant.value.quantityAvailable >= currentQuantity.value
            ? currentQuantity.value
            : currentVariant.value.quantityAvailable
      }
    : null;
};

watch(
  () => props.patternProduct,
  (newVal) => {
    currentQuantity.value = newVal.quantity;
    currentVariant.value = newVal.product?.variant || null;
  },
  { deep: true }
);

// TODO: refactor this so we don't use expose at all, and do everything uni-directionally, with emits or the cart store
defineExpose({
  getCartItem,
  getTrackingItem
});
</script>

<template>
  <div
    v-if="patternProduct.product"
    class="flex gap-2 border-b border-gray-200 pb-4 pt-1 last:border-none"
  >
    <div class="flex grow items-start gap-4">
      <a
        v-if="imgSrc"
        class="relative isolate shrink-0"
        :href="productPageURL"
        target="_blank"
      >
        <Image
          :src="imgSrc"
          img-classes="size-16"
          :lazy-load="true"
          :width="96"
          :height="96"
          loader="fade"
          class="overflow-hidden rounded-md bg-gray-50"
          :alt="patternProduct.product.title"
        />
        <SaleBadgeRound
          v-if="patternProduct.product.saleDiscountPercentage > 0"
          :discount-percentage="patternProduct.product.saleDiscountPercentage"
        />
      </a>

      <div class="flex h-full grow flex-col justify-center gap-0.5">
        <!-- Brand -->
        <span
          v-if="patternProduct.product?.brand"
          class="text-xs text-gray-500"
          >{{ patternProduct.product.brand }}</span
        >
        <!-- Title -->
        <a
          v-if="patternProduct.product?.handle"
          class="text-sm font-bold leading-tight"
          :href="productPageURL"
          target="_blank"
        >
          {{ patternProduct.product.title }}
        </a>

        <!-- Variant Selector -->
        <template v-if="patternProduct.product.variants.nodes.length > 1">
          <VariantSelector
            :product-type="patternProduct.product.productType"
            :options="patternProduct.product.variants.nodes"
            :active="currentVariant"
            :heading="t('patterns.products.select-variant')"
            @option-click="handleVariantChange"
          />
        </template>
        <template v-else>
          <StockIndicator
            v-if="currentVariant"
            class="flex text-xs"
            :in-stock="currentVariant.quantityAvailable"
          />
        </template>
      </div>
    </div>

    <div class="flex flex-col gap-2">
      <div class="flex justify-end">
        <NumberSpinner
          :model-value="currentQuantity ?? 0"
          :min="0"
          size="compact"
          class="bg-white"
          @update:model-value="handleQuantityChange"
        />
      </div>

      <div class="flex justify-end gap-2 text-sm">
        <span
          class="font-bold"
          :class="shouldShowCompareAtPrice ? 'text-secondary' : 'text-black'"
        >
          {{ priceWithCurrencySymbol }}
        </span>
        <span
          v-if="shouldShowCompareAtPrice"
          class="text-gray-400 line-through"
        >
          {{ compareAtPriceWithCurrencySymbol }}
        </span>
      </div>
    </div>
  </div>
</template>
